import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * 🚨 THE FIELD THAT HAD NO CONSUMER.
 *
 * cdMath's withDefault() has been recording every disclosure figure it had to
 * default, into `usedDefaults`, correctly and completely, for months — onto a
 * response nobody rendered. It is the third instance of that shape: the prime
 * rate's console.warn ran for 162 days, and the flat 18-day prepaid interest
 * sat inside this very array the whole time.
 *
 * 🔑 A ROW PER FIELD, NOT A COUNT. A defaulted prepaidTaxMonths and a defaulted
 * titleSettlement are different conversations — one is an estimate the rule
 * expects, the other is a zero-tolerance charge with no band beneath it. A
 * number collapses them into "some things are estimated", which is how this
 * stayed invisible while being technically reported.
 *
 * ⚠️ AND THE PHANTOM DISTINCTION IS RENDERED, because it is the difference
 * between a gap and a missing subsystem. ABSENT means the column exists and
 * this file has no value yet. PHANTOM means the input key is not a column at
 * all, so the constant is the only value the system can ever produce, on every
 * file. Nine of ten are phantoms. Showing them as ordinary gaps would
 * understate it in exactly the way usedDefaults itself did.
 */
export default function DisclosureDefaultsCard({ borrowerId, token }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [showIssued, setShowIssued] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await axios.get(
          `${API}/api/admin/borrowers/${borrowerId}/disclosure-defaults`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (alive) setData(d);
      } catch (e) {
        if (alive) setErr(e.response?.data?.error || 'Could not load disclosure defaults.');
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [borrowerId, token]);

  if (loading) return null;
  if (err) return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 18px', marginBottom: 16, fontSize: 13, color: '#9f1239' }}>{err}</div>
  );

  const set = showIssued && data?.atIssuance ? data.atIssuance : data?.live;
  const counts = set?.counts;
  const rows = (set?.rows || []).filter((r) => !r.closedSince || showIssued);

  const SEV = {
    fail: { bg: '#fff0f3', bd: '#fecdd3', fg: '#9f1239', chip: '#9f1239', label: 'NO TOLERANCE BAND' },
    warn: { bg: '#fff7ed', bd: '#fed7aa', fg: '#7a5200', chip: '#b45309', label: 'estimate permitted' },
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 18px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>Disclosure figures that are placeholders</div>
        {data?.atIssuance && (
          <button onClick={() => setShowIssued((v) => !v)}
            style={{ background: 'none', border: 'none', color: '#3a6ad9', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
            {showIssued ? 'Show current' : 'Show what the issued LE carried'}
          </button>
        )}
      </div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14, lineHeight: 1.6 }}>
        {showIssued
          ? `As recorded on the frozen Loan Estimate snapshot${data.issuedAt ? ` of ${new Date(data.issuedAt).toLocaleDateString()}` : ''}. Read under classification table v${set?.version ?? 1}, the version in force when it was issued. Rows marked CLOSED SINCE have been fixed in the meantime.`
          : 'What a disclosure generated right now would fall back to. Each figure the loan cannot source is listed with the rule that governs it.'}
      </div>

      {data?.liveError && !showIssued && (
        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#7a5200', lineHeight: 1.6 }}>
          ⏳ {data.liveError}
        </div>
      )}

      {counts && (
        <div style={{ display: 'flex', gap: 18, marginBottom: 14, fontSize: 12, color: '#374151', flexWrap: 'wrap' }}>
          <span><strong style={{ fontSize: 15 }}>{counts.live}</strong> placeholder figure{counts.live === 1 ? '' : 's'}</span>
          {counts.failing > 0 && <span style={{ color: '#9f1239' }}><strong style={{ fontSize: 15 }}>{counts.failing}</strong> with NO tolerance band</span>}
          {counts.phantom > 0 && <span style={{ color: '#9f1239' }}><strong style={{ fontSize: 15 }}>{counts.phantom}</strong> with no input path at all</span>}
        </div>
      )}

      {rows.map((r) => {
        const s = SEV[r.severity] || SEV.warn;
        return (
          <div key={r.field} style={{ background: r.closedSince ? '#f9fafb' : s.bg, border: `1px solid ${r.closedSince ? '#e5e7eb' : s.bd}`,
                                      borderRadius: 9, padding: '11px 13px', marginBottom: 8, opacity: r.closedSince ? 0.65 : 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 5 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{r.field}</span>
              <span style={{ fontSize: 10, fontWeight: 800, color: r.closedSince ? '#6b7280' : s.chip, letterSpacing: 0.3 }}>
                {r.closedSince ? 'CLOSED SINCE' : s.label}
              </span>
              {/* ⚠️ PHANTOM IS ITS OWN CHIP. "No value on this file yet" and "no
                  column exists, so there never will be" are different problems
                  and must not read the same. */}
              {!r.closedSince && r.phantom && (
                <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', background: '#9f1239', borderRadius: 4, padding: '2px 6px', letterSpacing: 0.3 }}>
                  NO INPUT PATH
                </span>
              )}
              {!r.closedSince && r.phantom === false && (
                <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280' }}>awaiting a value</span>
              )}
            </div>
            <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.7 }}>
              <div><strong>Using:</strong> {r.fallback || '—'}</div>
              <div><strong>Bucket:</strong> {r.bucketLabel}{r.provision ? ` — ${r.provision}` : ''}</div>
              {r.phantom && !r.closedSince && (
                <div style={{ color: '#9f1239' }}>
                  <strong>Input:</strong> <code>{r.input}</code> is not a column — nothing can ever supply this figure.
                </div>
              )}
              <div style={{ color: '#6b7280', marginTop: 3 }}>{r.why}</div>
            </div>
          </div>
        );
      })}

      {counts?.failing > 0 && !showIssued && (
        <div style={{ marginTop: 12, background: '#fff0f3', border: '2px solid #fecdd3', borderRadius: 9, padding: '12px 14px', fontSize: 12, color: '#9f1239', lineHeight: 1.7 }}>
          🚨 <strong>Launch gate.</strong> A zero-tolerance charge disclosed from a placeholder cannot be
          cured: the figure on the Loan Estimate is a ceiling, and a vendor invoice arriving is not a
          changed circumstance under §1026.19(e)(3)(iv). Doorly cannot close a loan until these come
          from real sources — see <code>docs/launch-gates.md</code>.
        </div>
      )}
    </div>
  );
}
